import { reportClientError } from '@smiledragon-corp/signoz-nextjs/react';
import { configure } from 'safe-stable-stringify';

/**
 * 이미 처리된(caught) 예외를 SigNoz에 warn 레벨로 남긴다.
 *
 * - 이 헬퍼는 `try/catch` · React Query `onError` 등이 **이미 우아한 UI(모달·에러 표시)로 흡수한**
 *   예외 전용이다. 미처리 에러는 `SignozProvider`가 `window.onerror` / `unhandledrejection` 로
 *   자동 캡처하며 그건 error 레벨이다. 여기서 다시 올리지 마.
 * - `origin`(frontend/backend)을 자동 태깅하는 이유: 백엔드 로그에 남지 않는 **프론트엔드에서만
 *   발생하는** API 문제(네트워크/전송/파싱/클라 예외)를 SigNoz에서 `origin=frontend`로 필터하기
 *   위함이다.
 * - 왜 원본 error를 합성 `Error`로 감싸는가: `reportClientError`는 `error`가 `Error`면
 *   message/name/stack을 **거기서 파생**하고 형제 `message` 필드를 무시하며, `[message, stack]`으로
 *   dedupe한다. 따라서 scope+context를 붙이려면 message에 접어 넣는 수밖에 없다.
 * - payload에 구조화된 context 필드가 없으므로 message를 **JSON 오브젝트 문자열**로 만든다.
 *   백엔드(pino)도 `body`에 JSON 문자열을 싣기 때문에 SigNoz에서 양쪽을 같은 방식으로
 *   파싱·필터할 수 있다. `k=v` 평문은 값에 공백이 섞이면 파싱이 깨졌다.
 */

export type WarnContext = Record<string, string | number | boolean | null | undefined>;

/**
 * payload 직렬화기. `JSON.stringify`를 쓰지 않는 이유는 두 가지다.
 *
 * 1. **순환 참조·BigInt에 던지지 않는다** — 이 함수는 react-query의 `QueryCache.onError` /
 *    `MutationCache.onError`와 에러 바운더리의 `componentDidCatch` 안에서 돈다. `JSON.stringify`는
 *    순환 참조와 BigInt에 `TypeError`를 던지므로, 거기서 throw가 나면 **관측 코드가 원래 에러를
 *    가로챈다.** 단 이건 "무슨 값이 와도 안 던진다"는 보장은 아니다 — 중첩 값의 `toJSON`이 던지면
 *    깊이 마스킹보다 먼저 호출되어 전파되고, 던지는 getter는 아래 `{ ...context }` 스프레드에서
 *    직렬화기에 닿기도 전에 탈출한다. 실제 입력(JSON 파싱된 axios `response.data`)엔 둘 다 없다.
 * 2. **PII 봉쇄** — `WarnContext`의 원시 타입 제한은 컴파일 타임 계약일 뿐이라 런타임에는
 *    객체가 도달할 수 있다(`error.response?.data` 파생 값 등). 통째로 직렬화되면 응답 body·DTO가
 *    그대로 관측 payload에 실린다.
 *
 * 두 옵션은 **둘 다 필수**다 — 하나라도 빠지면 에러도 경고도 없이 조용히 깨진다.
 *
 * - `deterministic: false` — 이 패키지의 기본 export는 **키를 알파벳 순으로 정렬한다.**
 *   그대로 쓰면 아래 키 순서 계약(`scope → origin → status → 호출자 context → message`)이
 *   조용히 깨진다.
 * - `maximumDepth: 1` — 내용이 있는 객체를 `"[Object]"`, 배열을 `"[Array]"`로 봉쇄하는 PII 2차
 *   방어선(빈 `{}`·`[]`는 깊이 검사 전에 그대로 나가지만 실을 PII가 없다).
 *   깊이 2 이상이면 1단계 필드(email, phone 등)가 그대로 노출된다. 순환 참조도 여기서 먼저 걸린다.
 *
 * 정상 payload(전부 원시 타입)에 대한 출력은 `JSON.stringify`와 byte-identical이다
 * (따옴표·개행·백슬래시 이스케이프 포함). 네이티브(viewup-native)와 lockstep으로 맞춘 계약이다.
 */
const stringifyPayload = configure({ deterministic: false, maximumDepth: 1 });

function classifyApiError(error: unknown): { origin: 'frontend' | 'backend'; status?: number } {
  if (error && typeof error === 'object') {
    const e = error as {
      response?: { status?: number };
      status?: number;
      isAxiosError?: boolean;
      request?: unknown;
    };
    const status = e.response?.status ?? e.status;
    if (typeof status === 'number') return { origin: 'backend', status };
    if (e.isAxiosError || e.request) return { origin: 'frontend' };
  }
  return { origin: 'frontend' };
}

export function reportHandledWarning(scope: string, error: unknown, context?: WarnContext): void {
  const { origin, status } = classifyApiError(error);
  const originalMessage = error instanceof Error ? error.message : String(error ?? '');

  // 키 순서는 JSON 문자열에 그대로 드러난다: scope → origin/status → 호출자 context → message.
  // `...context`가 origin/status를 덮어써도 키 위치는 앞자리를 유지한다(객체 리터럴 규칙).
  const merged: WarnContext = { scope, origin, status, ...context, message: originalMessage };
  const payload = Object.fromEntries(
    Object.entries(merged).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );

  const wrapped = new Error(stringifyPayload(payload));
  if (error instanceof Error) {
    wrapped.name = error.name;
    if (error.stack) wrapped.stack = error.stack;
    (wrapped as { cause?: unknown }).cause = error;
  }
  reportClientError({ error: wrapped, source: 'manual', level: 'warn' });
}
